import { Trans } from '@lingui/react/macro';
import { LifecycleEnum } from '@luminovo/api-contracts';
import { colorSystem, Flexbox, Text, Tooltip } from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';

import { getLifecycleColor, lifecycleEnumLongNamePublicTranslations } from '../chips';

export const lifecycleTooltipLabel: Record<LifecycleEnum, { description: JSX.Element }> = {
    [LifecycleEnum.Obsolete]: {
        description: <Trans>Part is no longer available.</Trans>,
    },
    [LifecycleEnum.EndOfLife]: {
        description: <Trans>Last time buy (LTB) has been announced and the part will become obsolete.</Trans>,
    },
    [LifecycleEnum.NotRecommendedForNewDesigns]: {
        description: (
            <Trans>
                Part is in active production, but in most cases, a new generation has been released and is preferred.
            </Trans>
        ),
    },
    [LifecycleEnum.PreRelease]: {
        description: (
            <Trans>The part is in process of qualification for release so the datasheet is subject to change.</Trans>
        ),
    },
    [LifecycleEnum.Active]: {
        description: <Trans>Part is in active production and is recommended for design-in.</Trans>,
    },
    [LifecycleEnum.Unknown]: {
        description: <Trans>The lifecycle information is not available or has not been determined yet.</Trans>,
    },
    [LifecycleEnum.Aftermarket]: {
        description: <Trans>Part is no longer available.</Trans>,
    },
    [LifecycleEnum.Acquired]: {
        description: <Trans>Part has been acquired by another company.</Trans>,
    },
};

export function LifecycleTooltip({ children }: { children: React.ReactElement }): JSX.Element {
    return (
        <Tooltip variant="white" arrow title={<LifecycleTooltipTitle />}>
            {children}
        </Tooltip>
    );
}

const getColorSystemColor = (lifecycle: LifecycleEnum) => {
    return colorSystem[getLifecycleColor(lifecycle)][5];
};

export const LifecycleTooltipTitle = ({
    maxHeight,
    getColor = getColorSystemColor,
}: {
    maxHeight?: string;
    getColor?: (lifecycle: LifecycleEnum) => string;
}): JSX.Element => {
    return (
        <Flexbox
            gap={4}
            sx={{
                flexDirection: 'column',
                padding: '4px',
                maxHeight: maxHeight,
                overflow: 'auto',
            }}
        >
            {Object.values(LifecycleEnum).map((status, index) => {
                const color = getColor(status);
                return (
                    <Flexbox
                        gap="4px"
                        key={index}
                        sx={{
                            flexDirection: 'column',
                            padding: '4px',
                        }}
                    >
                        <Flexbox
                            gap={4}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    borderRadius: 4,
                                    width: 8,
                                    height: 8,
                                    backgroundColor: color,
                                }}
                            />
                            <Text variant="h5">{transEnum(status, lifecycleEnumLongNamePublicTranslations)}</Text>
                        </Flexbox>
                        <Text variant="body-small" color={colorSystem.neutral[8]}>
                            {lifecycleTooltipLabel[status].description}
                        </Text>
                    </Flexbox>
                );
            })}
        </Flexbox>
    );
};
